import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import WatchImg from "../components/WatchImg";
import RelatedWatches from "../components/RelatedWatches";
import LightboxModal from "../components/LightboxModal";
import {
  IconWhatsApp,
  IconArrowLeft,
  IconChevronLeft,
  IconChevronRight,
} from "../components/Icons";
import { useGet, useMultipleApi } from "../api/useApi";
import { API_ROUTES } from "../api/apiRoutes";
import useSeo from "../seo/useSeo";
import { buildProductMeta } from "../seo/seoConfig";

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const hasFetchedAllProductDetailsData = useRef(false);
  const [watch, setWatch] = useState({});
  const [watchImages, setWatchImages] = useState([]);
  const [watchDetails, setWatchDetails] = useState({});
  const [watchCondition, setWatchCondition] = useState({});
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const goPrev = useCallback(
    () =>
      setActiveImg((i) =>
        watchImages.length
          ? (i - 1 + watchImages.length) % watchImages.length
          : 0,
      ),
    [watchImages.length],
  );
  const goNext = useCallback(
    () => setActiveImg((i) => (watchImages.length ? (i + 1) % watchImages.length : 0)),
    [watchImages.length],
  );

  // SEO — mirrors what netlify/edge-functions/seo.js injects server-side, so the
  // tags a crawler reads and the tags a browser ends up with stay identical.
  const seoMeta = useMemo(() => buildProductMeta(watch), [watch]);

  useSeo({
    title: seoMeta?.title,
    description: seoMeta?.description,
    image: seoMeta?.image,
    imageAlt: seoMeta?.imageAlt,
    type: "product",
    canonicalPath: `/watches/${id}`,
    ready: !!seoMeta,
    jsonLd: seoMeta
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: seoMeta.imageAlt,
          description: seoMeta.description,
          sku: seoMeta.product?.sku || undefined,
          image: seoMeta.image ? [seoMeta.image] : undefined,
          brand: seoMeta.product?.brand
            ? { "@type": "Brand", name: seoMeta.product.brand }
            : undefined,
          itemCondition:
            seoMeta.product?.condition && /new/i.test(seoMeta.product.condition)
              ? "https://schema.org/NewCondition"
              : "https://schema.org/UsedCondition",
          offers: {
            "@type": "Offer",
            priceCurrency: "IDR",
            availability: "https://schema.org/InStock",
            ...(seoMeta.product?.price
              ? { price: String(seoMeta.product.price) }
              : {}),
          },
        }
      : null,
  });

  // API Calls
  const { executeAll } = useMultipleApi();
  const { loading: loadingGetProductDetails, execute: getProductDetails } =
    useGet(API_ROUTES.WATCHES.GET_DETAILS(":product_id"), {
      onSuccess: (data) => {
        setWatch(data?.value);
        // Can be null (not just empty) when a product has no photos; keeping this
        // an array is what stops goPrev/goNext and the gallery from throwing.
        setWatchImages(
          Array.isArray(data?.value?.product_images)
            ? data.value.product_images
            : [],
        );
        setWatchDetails(data?.value?.product_watchdetail);
        setWatchCondition(data?.value?.product_condition);
      },
      onError: (error) => {
        console.error("Error fetching product details:", error);
      },
    });

  const {
    data: relatedProducts,
    loading: loadingGetRelatedProducts,
    execute: getRelatedProducts,
  } = useGet(API_ROUTES.WATCHES.GET_RELATED_WATCHES(":product_id"), {
    onError: (error) => {
      console.error("Error fetching related products:", error);
    },
  });

  useEffect(() => {
    if (id) {
      if (hasFetchedAllProductDetailsData.current) {
        return;
      }

      hasFetchedAllProductDetailsData.current = true;

      const fetchAllProductDetailsData = async () => {
        await executeAll([
          () => getProductDetails({ params: { product_id: id } }),
          () => getRelatedProducts({ params: { product_id: id } }),
        ]);
      };

      fetchAllProductDetailsData();
    }
  }, [id]);

  useEffect(() => {
    hasFetchedAllProductDetailsData.current = false;

    setWatch({});
    setWatchImages([]);
    setWatchDetails({});
    setWatchCondition({});
    setActiveImg(0);
  }, [id]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxOpen, goPrev, goNext]);

  // The API omits some values per watch (sent as missing/null); skip them rather than show an empty label.
  const isPresent = (value) =>
    typeof value === "string" ? value.trim() !== "" : value !== null && value !== undefined;

  const specRows = [
    {
      label: "Reference",
      value: watch?.product_reference?.reference,
    },
    {
      label: "Gender",
      value:
        watchDetails?.gender === "Female"
          ? "Women"
          : watchDetails?.gender === "Male"
            ? "Men"
            : (watchDetails?.gender ?? ""),
    },
    { label: "Condition", value: watchCondition?.category },
    { label: "Year", value: watchDetails?.year },
    { label: "Box & Papers", value: watchCondition?.package_content },
    { label: "Dial", value: watchDetails?.dial?.name },
    { label: "Dial Numerals", value: watchDetails?.dial_numeral?.name },
    { label: "Case Material", value: watchDetails?.case?.name },
    { label: "Case Diameter", value: watchDetails?.case_diameter?.name },
    { label: "Band Type", value: watchDetails?.band_description?.name },
    { label: "Band Material", value: watchDetails?.band?.name },
    { label: "Movement", value: watchDetails?.movement?.name },
    { label: "Caliber", value: watchDetails?.caliber?.name },
    { label: "Power Reserve", value: watchDetails?.power_reserve },
    { label: "Water Resistance", value: watchDetails?.water_resistance }
  ];

  const visibleSpecRows = specRows.filter((row) => isPresent(row.value));
  const referenceValue = watch?.product_reference?.reference;
  const conditionValue = watchCondition?.category;

  const whatsappMsg = watch?.is_show_price
    ? `Hi, I'd like to inquire about the price of the ${watch?.brand?.name} - ${watch?.name} (${watch?.sku}). Is it available?`
    : `Hi, I'm interested in the ${watch?.brand?.name} - ${watch?.name} (${watch?.sku}). Is it still available?`;

  const whatsappUrl = `https://wa.me/6285178228989?text=${encodeURIComponent(whatsappMsg)}`;

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <div className="all-watches-breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/watches">All Watches</Link>
        <span>/</span>
        <span>
          {watch?.brand?.name} {watch?.name}
        </span>
      </div>

      {/* Back Button */}
      <div className="detail-container-wrapper">
        <button className="detail-back-btn" onClick={() => navigate(-1)}>
          <IconArrowLeft /> Back
        </button>
      </div>

      {/* Product Detail */}
      {loadingGetProductDetails ? (
        <div className="spinner-container">
          <div className="spinner" />
        </div>
      ) : !watch ? (
        <div className="empty-state">
          <div className="empty-state-icon">⌚</div>
          <div className="empty-state-title empty-state-title--lg">
            Watch Not Found
          </div>
          <Link
            to="/watches"
            className="view-more"
            style={{ textDecoration: "none", display: "inline-block" }}
          >
            Browse All Watches
          </Link>
        </div>
      ) : (
        <div className="detail-container">
          {/* Image Gallery */}
          <div className="detail-gallery">
            <div
              className="detail-main-img"
              onClick={() => setLightboxOpen(true)}
              style={{ cursor: "zoom-in" }}
            >
              <WatchImg
                src={watchImages[activeImg]?.file_url}
                alt={watch?.name}
                className="img-cover"
              />
              {isPresent(conditionValue) && (
                <div className="product-condition">{conditionValue}</div>
              )}
              {watchImages?.length > 1 && (
                <>
                  <button
                    className="detail-img-arrow detail-img-arrow-left"
                    onClick={(e) => {
                      e.stopPropagation();
                      goPrev();
                    }}
                    aria-label="Previous image"
                  >
                    <IconChevronLeft />
                  </button>
                  <button
                    className="detail-img-arrow detail-img-arrow-right"
                    onClick={(e) => {
                      e.stopPropagation();
                      goNext();
                    }}
                    aria-label="Next image"
                  >
                    <IconChevronRight />
                  </button>
                  <div className="detail-img-dots">
                    {watchImages?.map((_, i) => (
                      <span
                        key={i}
                        className={`detail-img-dot ${i === activeImg ? "active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveImg(i);
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            {watchImages?.length > 1 && (
              <div className="detail-thumbs">
                {watchImages?.map((img, i) => (
                  <div
                    key={i}
                    className={`detail-thumb ${i === activeImg ? "active" : ""}`}
                    onClick={() => setActiveImg(i)}
                  >
                    <WatchImg
                      src={img?.file_url}
                      alt={`${watch.name} view ${i + 1}`}
                      className="img-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="detail-info">
            <div className="detail-brand">{watch?.brand?.name}</div>
            {isPresent(referenceValue) && (
              <div className="detail-reference">{referenceValue}</div>
            )}
            <h1 className="detail-name">{watch?.name}</h1>

            <div className="detail-divider" />

            {visibleSpecRows.length > 0 && (
              <>
                <div className="detail-specs">
                  {visibleSpecRows.map((row) => (
                    <div key={row.label} className="detail-spec">
                      <div className="detail-spec-label">{row.label}</div>
                      <div className="detail-spec-value">{row.value}</div>
                    </div>
                  ))}
                </div>

                <div className="detail-divider" />
              </>
            )}

            {watch?.is_show_price ? (
              <div className="detail-price">
                {" "}
                Rp {watch?.selling_price?.toLocaleString("id-ID")}
              </div>
            ) : (
              <div className="detail-ask-price">
                <div className="detail-ask-price-label">Price on Request</div>
                <p className="detail-ask-price-sub">
                  Contact us to receive a personalised quote for this
                  timepieces.
                </p>
              </div>
            )}

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="detail-cta"
              style={{ textDecoration: "none" }}
            >
              <IconWhatsApp />{" "}
              {watch?.is_show_price
                ? "Inquire via WhatsApp"
                : "Inquire for Price"}
            </a>

            <div className="detail-guarantees">
              <div className="detail-guarantee">
                ✦ 100% Authenticity Guaranteed
              </div>
              <div className="detail-guarantee">
                ◆ Complete Documentation Included
              </div>
              <div className="detail-guarantee">❖ Secure Transaction</div>
            </div>
          </div>
        </div>
      )}

      {/* Related Watches */}
      <RelatedWatches
        relatedProducts={relatedProducts}
        loadingGetRelatedProducts={loadingGetRelatedProducts}
      />

      {/* Lightbox Modal */}
      <LightboxModal
        lightboxOpen={lightboxOpen}
        setLightboxOpen={setLightboxOpen}
        watchImages={watchImages}
        activeImg={activeImg}
        setActiveImg={setActiveImg}
        goPrev={goPrev}
        goNext={goNext}
        watch={watch}
      />
    </div>
  );
};

export default ProductDetailPage;
